// live/stepView.ts (PF4) - the unified per-step view model. PF3 widened a live hand into `model.steps` = a
// PREFLOP decision (step 0, played-preflop path) followed by the postflop snapshots. This adaptor collapses
// either kind into ONE shape the DO-mode surfaces (LiveDiagnose / LiveSolo) render, so a surface drives a
// single flow regardless of street: the preflop branch delegates to preflopView, the postflop branch to the
// proven liveView helpers verbatim (so postflop behavior is byte-for-byte unchanged). The felt, ramp,
// bet-math tier, app-shown price, headline, two-axis grade, and the wrong-fold counterfactual all come out
// normalized - the surface never branches on step.kind itself.
import type { ActionKind } from '@postflop/schema';
import type { BetMathDef, PriceDef, PlayerTag, FamilyDef } from '../diagnose/data';
import type { RampAction } from '../engine/liveHand';
import type { ResolvedGrade } from '../grade/types';
import type { FeltView } from '../engine/bridge';
import {
  buildCounterfactual,
  buildPreflopCounterfactual,
  type Counterfactual,
  type LiveHandModel,
  type LiveSnapshot,
} from '../engine/liveRunner';
import { LIVE_FAMILIES, liveHeadline, livePrice, liveBetDef, buildLiveGrade } from './liveView';
import { preflopHeadline, preflopPrice, preflopBetDef, preflopRamp, buildPreflopGrade } from './preflopView';

export interface StepView {
  kind: 'preflop' | 'postflop';
  /** real street index (0..3) for the 4-pip StreetStepper. */
  streetIndex: number;
  street: string;
  felt: FeltView;
  /** the labeled, de-duplicated button set actually rendered (preflop-relabeled or the postflop ramp). */
  ramp: RampAction[];
  /** the manual bet-math config (null when no raise option - a river shove, or a preflop spot with no raise). */
  betDef: BetMathDef | null;
  /** the app-shown price + fading math aid. */
  price: PriceDef;
  headline: { kicker: string; h2: string; lede: string; tagSub: string };
  /** the five hand-family definitions for the read weighting - null preflop (no board yet). */
  families: FamilyDef[] | null;
  /** is there a bet/raise to call (Solo's typed-price gate + the required-equity readout)? */
  hasPrice: boolean;
  reqEq: number;
  /** true when the engine has a better line than folding - gates the optional wrong-fold counterfactual. */
  canReplayFold: boolean;
  /** grade the user's read + committed action into the two-axis reveal <GradeReveal> renders. */
  grade: (tag: PlayerTag | null, action: ActionKind, amount?: string) => ResolvedGrade;
  /** the wrong-fold "right line" replay (null when nothing to show - engine also folds / no preflop step). */
  counterfactual: () => Counterfactual | null;
}

/** Normalize one live step (preflop OR postflop) into a StepView. */
export const stepViewFor = (step: LiveSnapshot, model: LiveHandModel): StepView => {
  if (step.kind === 'preflop') {
    // an OPEN node has toCall = the big blind, but that is not a price to solve (hero is first in) - so
    // there is no typed-price gate there. Only a real raise faced (defend / vs-3bet) has a pot-odds price.
    const hasPrice = step.recommendation.role !== 'open' && step.price.toCall > 0;
    const reqEq = hasPrice ? Math.round((step.price.toCall / (step.price.pot + step.price.toCall)) * 100) : 0;
    // the counterfactual replays THIS node's recommended line - hero can face several preflop nodes, so it
    // indexes `preflops` the same way the postflop branch below indexes `snapshots`.
    const preflopIndex = model.preflops.indexOf(step);
    return {
      kind: 'preflop',
      streetIndex: step.streetIndex,
      street: step.street,
      felt: step.felt,
      ramp: preflopRamp(step),
      betDef: preflopBetDef(step),
      price: preflopPrice(step),
      headline: preflopHeadline(step, model),
      families: null,
      hasPrice,
      reqEq,
      canReplayFold: step.recommendation.action !== 'fold' && step.recommendation.finalIntent !== 'fold',
      grade: (tag, action, amount) => buildPreflopGrade(step, tag, action, amount),
      counterfactual: () => buildPreflopCounterfactual(model, preflopIndex),
    };
  }

  // postflop - the proven liveView path, unchanged. The counterfactual indexes the postflop snapshots array
  // (the step's position there, which is offset from its position in `steps` when a preflop step precedes).
  const hasPrice = step.price.toCall > 0;
  const reqEq = hasPrice ? Math.round((step.price.toCall / (step.price.pot + step.price.toCall)) * 100) : 0;
  const snapshotIndex = model.snapshots.indexOf(step);
  return {
    kind: 'postflop',
    streetIndex: step.streetIndex,
    street: step.street,
    felt: step.felt,
    ramp: step.ramp,
    betDef: liveBetDef(step),
    price: livePrice(step),
    headline: liveHeadline(step, model),
    families: LIVE_FAMILIES,
    hasPrice,
    reqEq,
    canReplayFold: step.bookAction !== 'fold',
    grade: (tag, action, amount) => buildLiveGrade(step, tag, action, amount).resolved,
    counterfactual: () => buildCounterfactual(model, snapshotIndex),
  };
};
